import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box, Typography, Paper, Button, TextField, Grid, Container, IconButton,
    Divider, Chip, Tooltip, LinearProgress, Tabs, Tab, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Avatar, Badge, useTheme, useMediaQuery,
    CircularProgress, Alert, Snackbar
} from '@mui/material';
import { motion } from 'framer-motion';
import {
    Share2, Copy, Mail, MessageCircle, Facebook, Twitter, Instagram, Gift,
    Award, TrendingUp, CheckCircle, Users, Clock, DollarSign, Zap, AlertCircle,
    RefreshCw, Link as LinkIcon
} from 'lucide-react';
import Sidebar from '../Sidebar';
import './Referral.css';
import { useAuth0 } from '@auth0/auth0-react';

// Background Blobs Component
const BackgroundBlobs = () => {
    return (
        <div className="background-blobs">
            <div className="blob blob-1"></div>
            <div className="blob blob-2"></div>
            <div className="blob blob-3"></div>
            <div className="blob blob-4"></div>
        </div>
    );
};

// ReferralStatsCard Component
const ReferralStatsCard = ({ icon, title, value, bgColor, textColor, isLoading }) => {
    const Icon = icon;

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                background: bgColor,
                height: '100%'
            }}
        >
            <Avatar
                sx={{
                    bgcolor: `${textColor}20`,
                    color: textColor,
                    width: 48,
                    height: 48
                }}
            >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : <Icon size={24} />}
            </Avatar>
            <Box>
                <Typography color="textSecondary" variant="body2">
                    {title}
                </Typography>
                <Typography variant="h5" fontWeight={600} sx={{ mt: 0.5 }}>
                    {isLoading ? "..." : value}
                </Typography>
            </Box>
        </Paper>
    );
};

// TabPanel Component
const TabPanel = (props) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`referral-tabpanel-${index}`}
            aria-labelledby={`referral-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
};

// Main Referral Component
const Referral = () => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [darkMode, setDarkMode] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [copied, setCopied] = useState(false);
    const [messageCopied, setMessageCopied] = useState(false);
    const [scrollPosition, setScrollPosition] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // Auth0 hook
    const { user, getAccessTokenSilently } = useAuth0();

    // State for referral data
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [referralCode, setReferralCode] = useState('');
    const [referralLink, setReferralLink] = useState('');
    const [referralStats, setReferralStats] = useState({
        totalReferred: 0,
        pendingReferrals: 0,
        totalRewards: '$0',
        successRate: '0%',
    });
    const [referralHistory, setReferralHistory] = useState([]);
    const [referralRewards, setReferralRewards] = useState({
        available_rewards: [],
        applied_rewards: [],
        pending_rewards: [],
        expired_rewards: [],
        total_available: 0,
        total_applied: 0
    });
    const [milestones, setMilestones] = useState([]);
    const [currentReferrals, setCurrentReferrals] = useState(0);
    const [nextMilestone, setNextMilestone] = useState(3);
    const [progressPercent, setProgressPercent] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    // Setup axios with authentication
    const setupAxios = async () => {
        try {
            const token = await getAccessTokenSilently();
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } catch (error) {
            console.error('Error setting up authentication:', error);
        }
    };

    // Load all referral data on component mount
    useEffect(() => {
        const loadAllData = async () => {
            await setupAxios();
            fetchReferralCode();
            fetchReferralStats();
            fetchReferralHistory();
            fetchReferralRewards();
        };

        loadAllData();

        // Setup scroll handler for parallax effects
        const handleScroll = () => {
            setScrollPosition(window.scrollY);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Function to generate/fetch referral code
    const fetchReferralCode = async () => {
        setLoading(true);
        try {
            const response = await axios.post('/api/referrals/generate-code/');
            setReferralCode(response.data.referral_code);
            setReferralLink(response.data.referral_link);
            setError(null);
        } catch (err) {
            console.error('Failed to generate referral code:', err);
            setError('Could not generate your referral code. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Function to fetch referral stats
    const fetchReferralStats = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/referrals/stats/');

            // Update referral stats
            setReferralStats({
                totalReferred: response.data.total_referred,
                pendingReferrals: response.data.pending_referrals,
                totalRewards: `$${response.data.total_rewards}`,
                successRate: response.data.success_rate,
            });

            // Update milestone data
            setCurrentReferrals(response.data.current_milestone_progress);
            setNextMilestone(response.data.next_milestone);

            // Calculate progress percentage (cap at 100%)
            const progress = Math.min((response.data.current_milestone_progress / response.data.next_milestone) * 100, 100);
            setProgressPercent(progress);

            // Update milestone information if available
            if (response.data.milestones) {
                setMilestones(response.data.milestones);
            }

            setError(null);
        } catch (err) {
            console.error('Failed to fetch referral stats:', err);
            setError('Could not load your referral statistics. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Function to fetch referral history
    const fetchReferralHistory = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/referrals/history/');
            setReferralHistory(response.data);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch referral history:', err);
            setError('Could not load your referral history. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Function to fetch referral rewards
    const fetchReferralRewards = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/referrals/rewards/');
            setReferralRewards(response.data);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch referral rewards:', err);
            // Don't set error here, as this is supplementary information
        } finally {
            setLoading(false);
        }
    };

    // Function to refresh all data
    const refreshAllData = async () => {
        setRefreshing(true);
        try {
            await setupAxios();
            await Promise.all([
                fetchReferralCode(),
                fetchReferralStats(),
                fetchReferralHistory(),
                fetchReferralRewards()
            ]);
        } catch (error) {
            console.error('Error refreshing data:', error);
        } finally {
            setRefreshing(false);
        }
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Copy referral link to clipboard
    const copyToClipboard = () => {
        navigator.clipboard.writeText(referralLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // Copy suggested message to clipboard
    const copyMessage = () => {
        const message = `Hey! I've been using Circledin to save money on my subscriptions. They help people form groups to get better rates on streaming, mobile plans, and more. We can both get $10 if you join with my link: ${referralLink}`;
        navigator.clipboard.writeText(message);
        setMessageCopied(true);
        setTimeout(() => setMessageCopied(false), 2000);
    };

    // Toggle dark mode
    const toggleDarkMode = () => {
        setDarkMode(!darkMode);
        document.body.classList.toggle('dark-mode');
    };

    // Open email client with referral link
    const shareViaEmail = () => {
        const subject = encodeURIComponent('Join Circledin and get $10 off');
        const body = encodeURIComponent(`Hey! I've been using Circledin to save money on my subscriptions. They help people form groups to get better rates on streaming, mobile plans, and more.

We can both get $10 if you join with my link: ${referralLink}

Hope to see you there!
${user?.name || ''}`);

        window.open(`mailto:?subject=${subject}&body=${body}`);
    };

    // Share on social media platforms
    const shareToSocial = (platform) => {
        let url;
        const text = encodeURIComponent("Join Circledin with my referral link and we'll both get $10!");
        const encodedUrl = encodeURIComponent(referralLink);

        switch (platform) {
            case 'facebook':
                url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${text}`;
                break;
            case 'twitter':
                url = `https://twitter.com/intent/tweet?text=${text}&url=${encodedUrl}`;
                break;
            case 'sms':
                url = `sms:?body=${encodeURIComponent(`Join Circledin with my referral link and we'll both get $10! ${referralLink}`)}`;
                break;
            default:
                return;
        }

        window.open(url, '_blank');
    };

    // Get status color
    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return '#4CAF50';
            case 'pending':
                return '#FFC107';
            case 'expired':
                return '#9E9E9E';
            case 'cancelled':
                return '#F44336';
            default:
                return '#9E9E9E';
        }
    };

    // Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    // Check if milestone is reached
    const isMilestoneReached = (target) => {
        return currentReferrals >= target;
    };

    return (
        <Box
            className={`referral-main-content ${darkMode ? 'dark-mode' : ''}`}
            sx={{ display: 'flex', minHeight: '100vh' }}
        >
            <div className="noise-overlay"></div>
            <BackgroundBlobs />

            {/* Sidebar Component */}
            <Sidebar
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                darkMode={darkMode}
            />

            {/* Main Content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { sm: `calc(100% - ${sidebarOpen ? 240 : 80}px)` },
                    ml: { sm: `${sidebarOpen ? 240 : 80}px` },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    zIndex: 2
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{
                        position: 'relative',
                        zIndex: 2,
                        pt: 1
                    }}
                >
                    {/* Refresh Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshCw size={16} className={refreshing ? 'spin-animation' : ''} />}
                            onClick={refreshAllData}
                            disabled={refreshing || loading}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                borderColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                                color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                            }}
                        >
                            {refreshing ? 'Refreshing...' : 'Refresh'}
                        </Button>
                    </Box>

                    {/* Error Alert */}
                    {error && (
                        <Alert
                            severity="error"
                            sx={{ mb: 2, borderRadius: 2 }}
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={refreshAllData}
                                >
                                    RETRY
                                </Button>
                            }
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Referral Hero Section */}
                    <Paper
                        component={motion.div}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            overflow: 'hidden',
                            mb: 4,
                            position: 'relative',
                            background: 'linear-gradient(135deg, rgba(255, 71, 117, 0.95), rgba(255, 118, 118, 0.9))',
                            color: 'white',
                            boxShadow: darkMode ? '0 10px 30px rgba(0, 0, 0, 0.25)' : '0 10px 30px rgba(255, 71, 117, 0.15)'
                        }}
                    >
                        {loading && (
                            <LinearProgress
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: 4,
                                    zIndex: 10,
                                    '& .MuiLinearProgress-bar': {
                                        background: 'white'
                                    }
                                }}
                            />
                        )}
                        <Box sx={{ p: { xs: 3, md: 5 } }}>
                            <Grid container spacing={3} alignItems="center">
                                <Grid item xs={12} md={7}>
                                    <Typography variant="h3" fontWeight={700} gutterBottom>
                                        Give $10, Get $10
                                    </Typography>
                                    <Typography variant="h6" fontWeight={400} sx={{ mb: 3, opacity: 0.9 }}>
                                        Invite friends to Circledin and both of you will earn $10 when they join any circle!
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            gap: 2,
                                            mb: 2,
                                            width: '100%'
                                        }}
                                    >
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            value={referralLink}
                                            placeholder={loading ? "Loading your referral link..." : ""}
                                            InputProps={{
                                                readOnly: true,
                                                startAdornment: loading ? (
                                                    <CircularProgress size={20} sx={{ mr: 1, color: '#FF4775' }} />
                                                ) : (
                                                    <LinkIcon size={20} style={{ marginRight: 8, color: '#FF4775' }} />
                                                ),
                                                sx: {
                                                    bgcolor: 'white', // Changed from semi-transparent white
                                                    color: '#333', // Changed from white to dark gray
                                                    borderRadius: 2,
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'rgba(255, 255, 255, 0.7)',
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'white',
                                                    },
                                                    '& input::placeholder': {
                                                        color: 'rgba(0, 0, 0, 0.5)',
                                                    }
                                                }
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={copyToClipboard}
                                            disabled={loading || !referralLink}
                                            startIcon={copied ? <CheckCircle size={20} /> : <Copy size={20} />}
                                            sx={{
                                                bgcolor: 'white',
                                                color: '#FF4775',
                                                fontWeight: 600,
                                                '&:hover': {
                                                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                                                },
                                                px: 3,
                                                borderRadius: 2,
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {copied ? 'Copied!' : 'Copy Link'}
                                        </Button>
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            Share via:
                                        </Typography>
                                        <Tooltip title="Email">
                                            <IconButton
                                                size="small"
                                                sx={{ color: 'white' }}
                                                onClick={shareViaEmail}
                                                disabled={loading || !referralLink}
                                            >
                                                <Mail size={18} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="SMS">
                                            <IconButton
                                                size="small"
                                                sx={{ color: 'white' }}
                                                onClick={() => shareToSocial('sms')}
                                                disabled={loading || !referralLink}
                                            >
                                                <MessageCircle size={18} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Facebook">
                                            <IconButton
                                                size="small"
                                                sx={{ color: 'white' }}
                                                onClick={() => shareToSocial('facebook')}
                                                disabled={loading || !referralLink}
                                            >
                                                <Facebook size={18} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Twitter">
                                            <IconButton
                                                size="small"
                                                sx={{ color: 'white' }}
                                                onClick={() => shareToSocial('twitter')}
                                                disabled={loading || !referralLink}
                                            >
                                                <Twitter size={18} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={5}
                                    sx={{
                                        display: { xs: 'none', md: 'flex' },
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}
                                >
                                    <Box
                                        component={motion.div}
                                        animate={{
                                            y: [0, -10, 0],
                                            rotate: [0, 2, 0, -2, 0]
                                        }}
                                        transition={{
                                            duration: 5,
                                            repeat: Infinity,
                                            repeatType: "loop"
                                        }}
                                        sx={{
                                            position: 'relative',
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}
                                    >
                                        {/* Gift illustration using CSS */}
                                        <Box sx={{
                                            position: 'relative',
                                            width: 200,
                                            height: 200,
                                            bgcolor: 'white',
                                            borderRadius: 4,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)'
                                        }}>
                                            <Box sx={{
                                                position: 'absolute',
                                                width: '90%',
                                                height: '90%',
                                                border: '5px dashed #FF4775',
                                                borderRadius: 3,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center'
                                            }}>
                                                <Typography
                                                    variant="h2"
                                                    fontWeight={800}
                                                    color="#FF4775"
                                                >
                                                    $10
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>

                    {/* Referral Stats */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                            >
                                <ReferralStatsCard
                                    icon={Users}
                                    title="Total Referred"
                                    value={referralStats.totalReferred}
                                    bgColor={darkMode ? 'rgba(32, 33, 44, 0.7)' : 'rgba(255, 255, 255, 0.9)'}
                                    textColor="#2196F3"
                                    isLoading={loading}
                                />
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            >
                                <ReferralStatsCard
                                    icon={Clock}
                                    title="Pending Invites"
                                    value={referralStats.pendingReferrals}
                                    bgColor={darkMode ? 'rgba(32, 33, 44, 0.7)' : 'rgba(255, 255, 255, 0.9)'}
                                    textColor="#FFC107"
                                    isLoading={loading}
                                />
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                            >
                                <ReferralStatsCard
                                    icon={DollarSign}
                                    title="Total Rewards"
                                    value={referralStats.totalRewards}
                                    bgColor={darkMode ? 'rgba(32, 33, 44, 0.7)' : 'rgba(255, 255, 255, 0.9)'}
                                    textColor="#4CAF50"
                                    isLoading={loading}
                                />
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.4 }}
                            >
                                <ReferralStatsCard
                                    icon={TrendingUp}
                                    title="Success Rate"
                                    value={referralStats.successRate}
                                    bgColor={darkMode ? 'rgba(32, 33, 44, 0.7)' : 'rgba(255, 255, 255, 0.9)'}
                                    textColor="#FF4775"
                                    isLoading={loading}
                                />
                            </motion.div>
                        </Grid>
                    </Grid>

                    {/* Rewards Progress */}
                    <Paper
                        component={motion.div}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.5 }}
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            overflow: 'hidden',
                            mb: 4,
                            p: 3,
                            background: darkMode ? 'rgba(32, 33, 44, 0.7)' : 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(10px)',
                            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                            boxShadow: darkMode ? '0 10px 30px rgba(0, 0, 0, 0.2)' : '0 10px 30px rgba(0, 0, 0, 0.05)'
                        }}
                    >
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="h6" fontWeight={600}>
                                Referral Milestones
                            </Typography>
                            <Chip
                                icon={<Zap size={16} />}
                                label={loading ? "Loading..." : `${currentReferrals}/${nextMilestone} to next reward`}
                                sx={{
                                    background: 'linear-gradient(90deg, #FF4775, #FF7676)',
                                    color: 'white',
                                    fontWeight: 500,
                                    '& .MuiChip-icon': {
                                        color: 'white'
                                    }
                                }}
                            />
                        </Box>

                        <LinearProgress
                            variant="determinate"
                            value={loading ? 0 : progressPercent}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                mb: 3,
                                backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: '#FF4775',
                                    borderRadius: 4
                                }
                            }}
                        />

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: 3,
                                        border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                                        height: '100%',
                                        bgcolor: isMilestoneReached(3) ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            Early Adopter
                                        </Typography>
                                        {isMilestoneReached(3) && (
                                            <CheckCircle size={20} color="#4CAF50" />
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Refer 3 friends
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Gift size={18} color="#FF4775" />
                                        <Typography variant="body2" fontWeight={500}>
                                            $15 per referral (up from $10)
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: 3,
                                        border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                                        height: '100%',
                                        bgcolor: isMilestoneReached(5) ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            Circle Champion
                                        </Typography>
                                        {isMilestoneReached(5) && (
                                            <CheckCircle size={20} color="#4CAF50" />
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Refer 5 friends
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Gift size={18} color="#FF4775" />
                                        <Typography variant="body2" fontWeight={500}>
                                            50% off your next month
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: 3,
                                        border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {!isMilestoneReached(10) && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                bgcolor: 'rgba(0,0,0,0.05)',
                                                backdropFilter: 'blur(2px)',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                zIndex: 1
                                            }}
                                        >
                                            <Typography variant="body1" fontWeight={600}>
                                                {10 - currentReferrals} more referrals to unlock
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            Referral Legend
                                        </Typography>
                                        {isMilestoneReached(10) && (
                                            <CheckCircle size={20} color="#4CAF50" />
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Refer 10 friends
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto' }}>
                                        <Award size={18} color="#FF4775" />
                                        <Typography variant="body2" fontWeight={500}>
                                            One month completely free
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Available Rewards Section (if there are any) */}
                        {referralRewards.available_rewards.length > 0 && (
                            <Box sx={{ mt: 4 }}>
                                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                                    Available Rewards ({referralRewards.available_rewards.length})
                                </Typography>
                                <Grid container spacing={2}>
                                    {referralRewards.available_rewards.map((reward, index) => (
                                        <Grid item xs={12} sm={6} md={4} key={reward.id || index}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 3,
                                                    border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                                                    height: '100%',
                                                    bgcolor: 'rgba(76, 175, 80, 0.1)',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Typography variant="subtitle1" fontWeight={600}>
                                                        ${reward.amount}
                                                    </Typography>
                                                    <Chip
                                                        label="Available"
                                                        size="small"
                                                        sx={{
                                                            bgcolor: 'rgba(76, 175, 80, 0.2)',
                                                            color: '#4CAF50',
                                                            fontWeight: 500
                                                        }}
                                                    />
                                                </Box>
                                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                                    {reward.description}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto' }}>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Expires: {reward.expires_at ? formatDate(reward.expires_at) : 'Never'}
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}
                    </Paper>

                    {/* Referral History and How It Works */}
                    <Paper
                        component={motion.div}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.6 }}
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            overflow: 'hidden',
                            mb: 4,
                            background: darkMode ? 'rgba(32, 33, 44, 0.7)' : 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(10px)',
                            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                            boxShadow: darkMode ? '0 10px 30px rgba(0, 0, 0, 0.2)' : '0 10px 30px rgba(0, 0, 0, 0.05)'
                        }}
                    >
                        <Box sx={{ borderBottom: 1, borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
                            <Tabs
                                value={tabValue}
                                onChange={handleTabChange}
                                variant="fullWidth"
                                sx={{
                                    '& .MuiTabs-indicator': {
                                        backgroundColor: '#FF4775',
                                    },
                                    '& .MuiTab-root': {
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '0.9rem',
                                        py: 2
                                    },
                                    '& .Mui-selected': {
                                        color: '#FF4775',
                                    }
                                }}
                            >
                                <Tab label="Referral History" id="referral-tab-0" aria-controls="referral-tabpanel-0" />
                                <Tab label="How It Works" id="referral-tab-1" aria-controls="referral-tabpanel-1" />
                                <Tab label="Tips & FAQ" id="referral-tab-2" aria-controls="referral-tabpanel-2" />
                            </Tabs>
                        </Box>

                        {/* Referral History Tab */}
                        <TabPanel value={tabValue} index={0}>
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <TableContainer>
                                    <Table sx={{ minWidth: 650 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Date</TableCell>
                                                <TableCell align="right">Reward</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {referralHistory.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center">
                                                        <Box sx={{ py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                            <Users size={40} color="#ddd" />
                                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                                No referrals yet
                                                            </Typography>
                                                            <Typography variant="body2" color="textSecondary">
                                                                Share your referral link to get started!
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                referralHistory.map((referral) => (
                                                    <TableRow
                                                        key={referral.id}
                                                        sx={{
                                                            '&:last-child td, &:last-child th': { border: 0 },
                                                            transition: 'background-color 0.2s',
                                                            '&:hover': {
                                                                backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                                                            }
                                                        }}
                                                    >
                                                        <TableCell component="th" scope="row">
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#FF4775' }}>
                                                                    {referral.name?.charAt(0) || 'U'}
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="body2" fontWeight={500}>
                                                                        {referral.name}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        {referral.email}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={referral.status === 'completed' ? 'Completed' : referral.status === 'pending' ? 'Pending' : referral.status.charAt(0).toUpperCase() + referral.status.slice(1)}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: `${getStatusColor(referral.status)}20`,
                                                                    color: getStatusColor(referral.status),
                                                                    fontWeight: 500
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{formatDate(referral.date)}</TableCell>
                                                        <TableCell align="right">
                                                            <Typography
                                                                fontWeight={600}
                                                                color={referral.status === 'completed' ? '#4CAF50' : ''}
                                                            >
                                                                {referral.reward}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </TabPanel>

                        {/* How It Works Tab */}
                        <TabPanel value={tabValue} index={1}>
                            <Box sx={{ p: 2 }}>
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                    The Circledin Referral Program
                                </Typography>

                                <Grid container spacing={4} sx={{ mt: 2 }}>
                                    <Grid item xs={12} md={4}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                textAlign: 'center',
                                                height: '100%'
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 60,
                                                    height: 60,
                                                    borderRadius: '50%',
                                                    bgcolor: 'rgba(255, 71, 117, 0.1)',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    mb: 2
                                                }}
                                            >
                                                <Share2 size={24} color="#FF4775" />
                                            </Box>
                                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                                1. Share Your Link
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Send your unique referral link to friends via email, text, or social media. Each person gets their own link to track referrals.
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                textAlign: 'center',
                                                height: '100%'
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 60,
                                                    height: 60,
                                                    borderRadius: '50%',
                                                    bgcolor: 'rgba(255, 71, 117, 0.1)',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    mb: 2
                                                }}
                                            >
                                                <Users size={24} color="#FF4775" />
                                            </Box>
                                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                                2. Friend Joins Circledin
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                When your friend signs up using your link, they get 50% off their first month ($6 for monthly or $4 for annual plan).
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                textAlign: 'center',
                                                height: '100%'
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 60,
                                                    height: 60,
                                                    borderRadius: '50%',
                                                    bgcolor: 'rgba(255, 71, 117, 0.1)',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    mb: 2
                                                }}
                                            >
                                                <Gift size={24} color="#FF4775" />
                                            </Box>
                                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                                3. Both Get Rewarded
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                After they make their first payment, you'll both receive your rewards. You get $10 credit applied to your next bill automatically.
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>

                                <Box
                                    sx={{
                                        mt: 4,
                                        p: 3,
                                        borderRadius: 3,
                                        border: '1px dashed',
                                        borderColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                                        bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <AlertCircle size={20} color="#FF4775" />
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            Important Details
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        • Referral rewards are credited directly to your account
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        • The referred friend must join a circle and make at least one payment
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        • Credits expire after 12 months if unused
                                    </Typography>
                                    <Typography variant="body2">
                                        • Your reward increases with more successful referrals
                                    </Typography>
                                </Box>
                            </Box>
                        </TabPanel>

                        {/* Tips & FAQ Tab */}
                        <TabPanel value={tabValue} index={2}>
                            <Box sx={{ p: 2 }}>
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                    Tips for Successful Referrals
                                </Typography>

                                <Grid container spacing={3} sx={{ mb: 4 }}>
                                    <Grid item xs={12} md={6}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                borderRadius: 3,
                                                border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                                                height: '100%'
                                            }}
                                        >
                                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                                Suggested Message
                                            </Typography>
                                            <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                                                "Hey! I've been using Circledin to save money on my subscriptions. They help people form groups to get better rates on streaming, mobile plans, and more. We can both get $10 if you join with my link: {referralLink}"
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                startIcon={messageCopied ? <CheckCircle size={16} /> : <Copy size={16} />}
                                                size="small"
                                                onClick={copyMessage}
                                                disabled={loading || !referralLink}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    borderColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                                                    color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                                                }}
                                            >
                                                {messageCopied ? 'Copied!' : 'Copy message'}
                                            </Button>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                borderRadius: 3,
                                                border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                                                height: '100%'
                                            }}
                                        >
                                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                                Best People to Refer
                                            </Typography>
                                            <Typography variant="body2" paragraph>
                                                • Family members looking to save on their plans
                                            </Typography>
                                            <Typography variant="body2" paragraph>
                                                • Friends who share multiple subscriptions
                                            </Typography>
                                            <Typography variant="body2" paragraph>
                                                • Colleagues who might want to join a work circle
                                            </Typography>
                                            <Typography variant="body2">
                                                • College students looking to save money
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>

                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                    Frequently Asked Questions
                                </Typography>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                        How long does it take to receive my reward?
                                    </Typography>
                                    <Typography variant="body2" paragraph>
                                        Rewards are credited to your account within 24 hours after your referred friend makes their first payment. The credit will automatically apply to your next bill.
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                        Is there a limit to how many people I can refer?
                                    </Typography>
                                    <Typography variant="body2" paragraph>
                                        There's no limit! You can refer as many people as you want and continue earning rewards. In fact, the more people you refer, the better your rewards become.
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                        What if someone signs up but doesn't use my referral link?
                                    </Typography>
                                    <Typography variant="body2" paragraph>
                                        Unfortunately, we can only track and reward referrals that come through your unique link. Make sure your friends use your referral link when signing up to ensure you both get rewarded.
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                        Can I withdraw my rewards as cash?
                                    </Typography>
                                    <Typography variant="body2" paragraph>
                                        Currently, referral rewards are applied as account credits toward your future Circledin payments. They cannot be withdrawn as cash but will automatically reduce your upcoming bills.
                                    </Typography>
                                </Box>
                            </Box>
                        </TabPanel>
                    </Paper>
                </Container>
            </Box>

            {/* Snackbar for notifications */}
            <Snackbar
                open={copied || messageCopied}
                autoHideDuration={2000}
                onClose={() => {
                    setCopied(false);
                    setMessageCopied(false);
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" variant="filled">
                    {copied ? 'Referral link copied to clipboard!' : 'Message copied to clipboard!'}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Referral;